import { useState } from 'react'
import { supabase } from '../lib/supabase.js'

// Password reset deeplink screen.
//
// Arrives here when Supabase's onAuthStateChange fires PASSWORD_RECOVERY
// — happens when a manager clicks the reset link in their email. The
// link's hash carries an access token; Supabase JS handles the token
// exchange automatically and just notifies us via the event.
//
// The screen takes a new password + confirm, calls updateUser, and
// then routes the manager into the app proper (already logged in
// thanks to the recovery session).
export default function PasswordResetScreen({ onDone }) {
  const [password, setPassword]   = useState('')
  const [confirm, setConfirm]     = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]         = useState(null)
  const [info, setInfo]           = useState(null)

  async function handleSubmit(e) {
    e?.preventDefault?.()
    if (submitting) return
    if (!password || !confirm) {
      setError('Type and confirm your new password.')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const { error: updErr } = await supabase.auth.updateUser({ password })
      if (updErr) throw updErr
      setInfo('Password updated. You can keep using the app.')
      // Acknowledge the recovery event — App will route us into the
      // tab UI now that the session is a normal one.
      onDone?.()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[PasswordResetScreen] updateUser failed', err)
      setError(`Could not update password: ${err?.message || 'try again.'}`)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleCancel() {
    // Sign the recovery session out so the manager lands back on the
    // login screen instead of being half-authenticated.
    try {
      await supabase.auth.signOut()
    } catch {
      // best effort — onAuthStateChange will reflect logged-out state
    }
    onDone?.()
  }

  return (
    <div className="min-h-full bg-white">
      <header className="bg-amber-brand-50 text-amber-brand-800 safe-top">
        <div className="mx-auto max-w-screen-sm px-4 py-4 flex items-center gap-3">
          <img
            src="/logo.png"
            alt=""
            className="h-12 w-12 rounded-xl shadow-md flex-shrink-0"
          />
          <div className="min-w-0">
            <div className="text-xs font-medium opacity-90">
              AI Restaurant Manager
            </div>
            <div className="text-base font-medium">Set a new password</div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-screen-sm px-4 pt-6 pb-8">
        <p className="text-sm leading-relaxed text-black/70">
          You arrived here from the password reset email. Choose a new
          password to finish.
        </p>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          {error && (
            <div role="alert" className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
          {info && (
            <div role="status" className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white shadow">
              {info}
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="reset-password">New password</label>
            <input
              id="reset-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="reset-confirm">Confirm</label>
            <input
              id="reset-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Updating\u2026' : 'Update password'}
          </button>

          <button
            type="button"
            onClick={handleCancel}
            className="block w-full text-center text-sm font-medium text-black/55 hover:text-black/80"
          >
            Cancel and sign out
          </button>
        </form>
      </main>
    </div>
  )
}
